import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, X, LogOut, ChevronUp, ChevronDown, MessageSquare, CalendarDays, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const navigate = useNavigate();

  const [newApartment, setNewApartment] = useState({
    name: '',
    type: ''
  });

  const [editApartment, setEditApartment] = useState({
    name: '',
    type: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchApartments();
  }, [navigate]);

  const fetchApartments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/apartments`, {
        headers: getAuthHeaders()
      });
      setApartments(response.data.apartments || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
      toast.error('Failed to load apartments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddApartment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/apartments`,
        newApartment,
        { headers: getAuthHeaders() }
      );
      toast.success('Apartment added successfully!');
      setIsAddDialogOpen(false);
      setNewApartment({ name: '', type: '' });
      fetchApartments();
    } catch (error) {
      toast.error('Failed to add apartment');
    }
  };

  const handleEditApartment = async (e) => {
    e.preventDefault();
    if (!selectedApartment) return;

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/apartments/${selectedApartment.id}`,
        editApartment,
        { headers: getAuthHeaders() }
      );
      toast.success('Apartment updated successfully!');
      setIsEditDialogOpen(false);
      setSelectedApartment(null);
      fetchApartments();
    } catch (error) {
      toast.error('Failed to update apartment');
    }
  };

  const handleDeleteApartment = async (apartmentId) => {
    if (!window.confirm('Are you sure you want to delete this apartment? All images will be deleted.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/apartments/${apartmentId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Apartment deleted successfully!');
      fetchApartments();
    } catch (error) {
      toast.error('Failed to delete apartment');
    }
  };

  const handleImageUpload = async (apartmentId, file, alt) => {
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', alt);

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/apartments/${apartmentId}/images`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Image uploaded successfully!');
      fetchApartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (apartmentId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/apartments/${apartmentId}/images?image_url=${encodeURIComponent(imageUrl)}`,
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Image deleted successfully!');
      fetchApartments();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleReorderImages = async (apartmentId, imageUrls) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/apartments/${apartmentId}/images/reorder`,
        { image_urls: imageUrls },
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Images reordered! First 3 will show on homepage.');
      await fetchApartments();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder images');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const openEditDialog = (apartment) => {
    setSelectedApartment(apartment);
    setEditApartment({ name: apartment.name, type: apartment.type });
    setIsEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-amber-900">Marina Homes Admin</h1>
              <p className="text-gray-600 text-sm">Manage your apartments and images</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/admin/reviews')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="manage-reviews-btn"
              >
                <MessageSquare size={18} className="mr-2" />
                Manage Reviews
              </Button>
              <Button
                onClick={() => navigate('/admin/bookings')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CalendarDays size={18} className="mr-2" />
                Guest Bookings
              </Button>
              <Button
                onClick={() => navigate('/admin/content')}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <FileText size={18} className="mr-2" />
                Edit Content
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-amber-600 text-amber-700 hover:bg-amber-50"
              >
                View Website
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-600 text-red-700 hover:bg-red-50"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Apartment Button */}
        <div className="mb-8">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                <Plus size={20} className="mr-2" />
                Add New Apartment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Apartment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddApartment} className="space-y-4">
                <div>
                  <Label htmlFor="name">Apartment Name</Label>
                  <Input
                    id="name"
                    value={newApartment.name}
                    onChange={(e) => setNewApartment({ ...newApartment, name: e.target.value })}
                    placeholder="e.g., Apartment 1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="type">Apartment Type</Label>
                  <Input
                    id="type"
                    value={newApartment.type}
                    onChange={(e) => setNewApartment({ ...newApartment, type: e.target.value })}
                    placeholder="e.g., Studio Deluxe"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
                  Add Apartment
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Apartments List */}
        {apartments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-600 mb-4">No apartments yet. Add your first apartment!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {apartments.map((apartment) => (
              <ApartmentCard
                key={`${apartment.id}-${apartment.images?.length || 0}-${apartment.images?.[0]?.url || ''}`}
                apartment={apartment}
                onEdit={openEditDialog}
                onDelete={handleDeleteApartment}
                onUploadImage={handleImageUpload}
                onDeleteImage={handleDeleteImage}
                onReorderImages={handleReorderImages}
                uploadingImage={uploadingImage}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Apartment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditApartment} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Apartment Name</Label>
              <Input
                id="edit-name"
                value={editApartment.name}
                onChange={(e) => setEditApartment({ ...editApartment, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-type">Apartment Type</Label>
              <Input
                id="edit-type"
                value={editApartment.type}
                onChange={(e) => setEditApartment({ ...editApartment, type: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
              Update Apartment
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Apartment Card Component
const ApartmentCard = ({ apartment, onEdit, onDelete, onUploadImage, onDeleteImage, onReorderImages, uploadingImage }) => {
  const [imageFile, setImageFile] = useState(null);
  const [imageAlt, setImageAlt] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [localImages, setLocalImages] = useState(apartment.images || []);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local images with props whenever apartment images change
  const apartmentImagesJson = JSON.stringify((apartment.images || []).map(img => img.url));
  useEffect(() => {
    setLocalImages(apartment.images || []);
    setIsSaving(false);
  }, [apartmentImagesJson]);

  const moveImageUp = (index) => {
    if (index === 0) return;
    const newImages = [...localImages];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    setLocalImages(newImages);
  };

  const moveImageDown = (index) => {
    if (index === localImages.length - 1) return;
    const newImages = [...localImages];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    setLocalImages(newImages);
  };

  const saveImageOrder = () => {
    setIsSaving(true);
    const imageUrls = localImages.map(img => img.url);
    onReorderImages(apartment.id, imageUrls);
  };

  const hasOrderChanged = () => {
    if (isSaving) return false; // Hide button while saving
    const currentUrls = (apartment.images || []).map(img => img.url).join(',');
    const localUrls = localImages.map(img => img.url).join(',');
    return currentUrls !== localUrls;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setImageFile(file);
    }
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });
    setBulkFiles(validFiles);
  };

  const handleUpload = async () => {
    if (!imageFile || !imageAlt) {
      toast.error('Please select a file and enter description');
      return;
    }

    await onUploadImage(apartment.id, imageFile, imageAlt);
    setImageFile(null);
    setImageAlt('');
    setShowUploadForm(false);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setUploadingBulk(true);
    const formData = new FormData();
    bulkFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const token = localStorage.getItem('adminToken');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/apartments/${apartment.id}/images/bulk`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { uploaded, failed, errors } = response.data;
      
      if (uploaded > 0) {
        toast.success(`Successfully uploaded ${uploaded} image${uploaded > 1 ? 's' : ''}!`);
      }
      
      if (failed > 0) {
        errors.forEach(err => {
          toast.error(`${err.file}: ${err.error}`);
        });
      }

      setBulkFiles([]);
      setShowBulkUpload(false);
      
      // Refresh apartment list
      window.location.reload();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploadingBulk(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Apartment Header */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-6 py-4 flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-white">{apartment.name}</h3>
          <p className="text-amber-100">{apartment.type}</p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => onEdit(apartment)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            <Edit size={16} />
          </Button>
          <Button
            onClick={() => onDelete(apartment.id)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-red-500"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>

      {/* Images Section */}
      <div className="p-6">
        <div className="mb-4 flex space-x-2">
          <Button
            onClick={() => {
              setShowUploadForm(!showUploadForm);
              setShowBulkUpload(false);
            }}
            variant="outline"
            className="border-amber-600 text-amber-700 hover:bg-amber-50"
          >
            <Upload size={18} className="mr-2" />
            Upload Single Image
          </Button>
          <Button
            onClick={() => {
              setShowBulkUpload(!showBulkUpload);
              setShowUploadForm(false);
            }}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Upload size={18} className="mr-2" />
            Bulk Upload
          </Button>
        </div>

        {/* Single Upload Form */}
        {showUploadForm && (
          <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="space-y-3">
              <div>
                <Label htmlFor={`file-${apartment.id}`}>Select Image</Label>
                <Input
                  id={`file-${apartment.id}`}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Max size: 5MB. Formats: JPG, PNG, WebP</p>
              </div>
              <div>
                <Label htmlFor={`alt-${apartment.id}`}>Image Description</Label>
                <Input
                  id={`alt-${apartment.id}`}
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                  placeholder="e.g., Spacious bedroom with king bed"
                  className="mt-1"
                />
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={handleUpload}
                  disabled={uploadingImage || !imageFile || !imageAlt}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {uploadingImage ? 'Uploading...' : 'Upload'}
                </Button>
                <Button
                  onClick={() => {
                    setShowUploadForm(false);
                    setImageFile(null);
                    setImageAlt('');
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Upload Form */}
        {showBulkUpload && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="space-y-3">
              <div>
                <Label htmlFor={`bulk-${apartment.id}`}>Select Multiple Images</Label>
                <Input
                  id={`bulk-${apartment.id}`}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={handleBulkFileSelect}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Select multiple images (max 5MB each). Filename will be used as description.
                </p>
              </div>
              {bulkFiles.length > 0 && (
                <div className="bg-white p-3 rounded border border-green-300">
                  <p className="text-sm font-medium mb-2">Selected: {bulkFiles.length} file(s)</p>
                  <ul className="text-xs text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                    {bulkFiles.map((file, idx) => (
                      <li key={idx}>• {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex space-x-2">
                <Button
                  onClick={handleBulkUpload}
                  disabled={uploadingBulk || bulkFiles.length === 0}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {uploadingBulk ? `Uploading ${bulkFiles.length} images...` : `Upload ${bulkFiles.length} Image${bulkFiles.length > 1 ? 's' : ''}`}
                </Button>
                <Button
                  onClick={() => {
                    setShowBulkUpload(false);
                    setBulkFiles([]);
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Images Grid */}
        {apartment.images && apartment.images.length > 0 ? (
          <>
            {/* Reorder Info Banner */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Homepage Display:</strong> The first 3 images will be shown on your website. 
                Use the ↑↓ arrows to reorder images.
              </p>
              {hasOrderChanged() && (
                <Button
                  onClick={saveImageOrder}
                  className="mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                  size="sm"
                >
                  Save New Order
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {localImages.map((image, index) => (
                <div key={index} className="relative group bg-gray-50 rounded-lg p-3 border-2 border-gray-200">
                  {/* Homepage Badge */}
                  {index < 3 && (
                    <div className="absolute top-2 left-2 z-10 bg-green-500 text-white px-2 py-1 rounded-md text-xs font-bold">
                      Homepage #{index + 1}
                    </div>
                  )}
                  
                  {/* Image */}
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-40 object-cover rounded-lg mb-2"
                  />
                  
                  {/* Image Info */}
                  <p className="text-xs text-gray-600 mb-2 truncate">{image.alt}</p>
                  
                  {/* Controls */}
                  <div className="flex items-center justify-between gap-2">
                    {/* Reorder Buttons */}
                    <div className="flex space-x-1">
                      <Button
                        onClick={() => moveImageUp(index)}
                        disabled={index === 0}
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0"
                        title="Move up"
                      >
                        <ChevronUp size={16} />
                      </Button>
                      <Button
                        onClick={() => moveImageDown(index)}
                        disabled={index === localImages.length - 1}
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0"
                        title="Move down"
                      >
                        <ChevronDown size={16} />
                      </Button>
                    </div>
                    
                    {/* Position Display */}
                    <span className="text-xs text-gray-500 font-medium">
                      #{index + 1}
                    </span>
                    
                    {/* Delete Button */}
                    <Button
                      onClick={() => onDeleteImage(apartment.id, image.url)}
                      size="sm"
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 h-8"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-gray-500 text-center py-8">No images yet. Upload your first image!</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
